/**
 * LinkPicker — searchable dropdown for choosing a record to link to.
 *
 * Renders its own "Add link" trigger button; clicking it opens a
 * Popover + Command dropdown of candidate records, grouped by type. The
 * caller supplies the candidate set (already filtered to active,
 * not-yet-linked records) and handles the selection via `onPick`.
 */

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  RECORD_TYPE_ICON,
  RECORD_TYPE_LABEL,
  type RecordRef,
  type RecordType,
} from "./linkShared";

const TYPE_ORDER: RecordType[] = [
  "asset",
  "creditor",
  "security_interest",
  "loan",
  "ato_sro",
];

interface LinkPickerProps {
  candidates: RecordRef[];
  onPick: (ref: RecordRef) => void;
  label?: string;
}

export function LinkPicker({
  candidates,
  onPick,
  label = "Add link",
}: LinkPickerProps) {
  const [open, setOpen] = useState(false);

  const groups = TYPE_ORDER.map((t) => ({
    type: t,
    items: candidates.filter((c) => c.type === t),
  })).filter((g) => g.items.length > 0);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1 text-xs">
          <Plus className="h-3.5 w-3.5" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-0"
        align="end"
        onWheel={(e) => e.stopPropagation()}
      >
        <Command
          // Plain substring filter over the title + subtitle we set as the
          // CommandItem value — feels like a regular search, not fuzzy.
          filter={(value, search) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            return value.toLowerCase().includes(q) ? 1 : 0;
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <CommandInput placeholder="Search records…" />
          <CommandList className="max-h-[320px] overscroll-contain">
            <CommandEmpty>No records available to link.</CommandEmpty>
            {groups.map((group) => {
              const TypeIcon = RECORD_TYPE_ICON[group.type];
              return (
                <CommandGroup
                  key={group.type}
                  heading={
                    <span className="flex items-center gap-1.5">
                      <TypeIcon className="h-3 w-3" />
                      {RECORD_TYPE_LABEL[group.type]}
                    </span>
                  }
                >
                  {group.items.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={`${item.title} ${item.subtitle ?? ""} ${item.id}`}
                      onSelect={() => {
                        onPick(item);
                        setOpen(false);
                      }}
                      className="gap-2 px-3 py-2"
                    >
                      <TypeIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="w-full truncate text-sm font-medium">
                          {item.title}
                        </span>
                        {item.subtitle && (
                          <span className="w-full truncate text-xs text-muted-foreground">
                            {item.subtitle}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
